import React, { useState, useEffect } from "react";
import { DataGrid, GridActionsCellItem, GridColDef } from "@mui/x-data-grid";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import Swal from "sweetalert2";
import FormRankingModal, {
  DataZodiacRanking,
} from "./components/FormRankingModal";
import { Add, Edit, Delete, Visibility } from "@mui/icons-material";
import { ENDPOINTS, getApiUrl } from "../../../common/endpoints";
import { Requests } from "../../../common/requests";
import { data_grid_styles } from "../../../constants/style";
import data_zodiac_rankings from "../../../data/zodiac/zodiac_ranking.json";
import { useSnackbar } from "notistack";
import { IS_SHOW_IMPORT } from "../../../constants";
import TogglePublicBtn from "../../../components/TogglePublicBtn";
const RankingOfConstellations = () => {
  const [rankings, setRankings] = useState<DataZodiacRanking[]>([]);
  const [selectedRanking, setSelectedRanking] =
    useState<DataZodiacRanking | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [openView, setOpenView] = useState<DataZodiacRanking | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  useEffect(() => {
    fetchRankings();
  }, []);

  const fetchRankings = async () => {
    const apiUrl = getApiUrl(ENDPOINTS.ZODIAC_RANKING_LIST);
    try {
      const data = await Requests.get(apiUrl);
      if (Array.isArray(data)) setRankings(data);
    } catch (e) {}
  };

  const handleOpenCreateModal = () => {
    setSelectedRanking(null);
    setIsEditMode(false);
    setOpenModal(true);
  };

  const handleOpenEditModal = (ranking: DataZodiacRanking) => {
    setSelectedRanking(ranking);
    setIsEditMode(true);
    setOpenModal(true);
  };

  const handleDelete = (rankingId: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const apiUrl = getApiUrl(
          ENDPOINTS.ZODIAC_RANKING_DELETE.replace(":id", rankingId)
        );
        await Requests.delete(apiUrl);
        fetchRankings();
        Swal.fire("Deleted!", "Your ranking has been deleted.", "success");
      }
    });
  };

  const onTogglePublish = async (id: string) => {
    try {
      const apiUrl = getApiUrl(
        ENDPOINTS.ZODIAC_RANKING_TOGGLE_PUBLISH.replace(":id", id)
      );
      await Requests.patch(apiUrl);
      fetchRankings();
      enqueueSnackbar("Toggle publish success!", { variant: "success" });
    } catch (e) {}
  };

  const columns: GridColDef[] = [
    { field: "name", headerName: "Name", width: 120 },
    {
      field: "ranking",
      headerName: "Ranking",
      width: 360,
      renderCell: (data) => {
        return (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
            }}
          >
            {data.value.map((item: string, index: number) => (
              <Typography>
                {item}
                {index !== data.value.length - 1 ? ", " : ""}
              </Typography>
            ))}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      type: "actions",
      width: 270,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<Edit />}
          label="Edit"
          onClick={() => handleOpenEditModal(params.row)}
        />,
        <GridActionsCellItem
          icon={<Delete />}
          label="Delete"
          onClick={() => handleDelete(params.id.toString())}
        />,
        <GridActionsCellItem
          icon={<Visibility />}
          label="View"
          onClick={() => setOpenView(params.row)}
        />,
        <TogglePublicBtn
          isValidated={!!params.row?.ispublished}
          onToggle={() => {
            onTogglePublish(params.row?.id);
          }}
        />,
      ],
    },
  ];

  const onImportData = async () => {
    setIsImporting(true);
    const apiUrl = getApiUrl(ENDPOINTS.ZODIAC_RANKING_BULK);
    try {
      const nb_pages = 20;
      const nbReq = data_zodiac_rankings.length / nb_pages;
      for (let i = 0; i <= nbReq; i++) {
        await Requests.post(
          apiUrl,
          data_zodiac_rankings.slice(i * nb_pages, (i + 1) * nb_pages)
        );
      }
      fetchRankings();
      enqueueSnackbar("Import successfully!", { variant: "success" });
    } catch (e) {
      enqueueSnackbar("Import unsuccessfully!", { variant: "error" });
    }
    setIsImporting(false);
  };

  return (
    <>
      {openModal ? (
        <FormRankingModal
          open={openModal}
          onClose={(isRefresh) => {
            setOpenModal(false);
            if (isRefresh) fetchRankings();
          }}
          ranking={selectedRanking}
          isEditMode={isEditMode}
        />
      ) : (
        <Box m={2}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              color="primary"
              variant="h5"
              sx={{
                fontWeight: "bold",
              }}
              gutterBottom
            >
              Ranking Of Constellations
            </Typography>
            <Box>
              <Button
                onClick={handleOpenCreateModal}
                variant="contained"
                color="primary"
                sx={{
                  textTransform: "unset",
                  marginRight: "10px",
                }}
                startIcon={<Add />}
              >
                Add
              </Button>
              {IS_SHOW_IMPORT && (
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    textTransform: "unset",
                  }}
                  onClick={() => {
                    onImportData();
                  }}
                  disabled={isImporting}
                >
                  {isImporting && (
                    <CircularProgress
                      size="30px"
                      sx={{
                        marginRight: "10px",
                        color: "#fff",
                      }}
                    />
                  )}
                  Import
                </Button>
              )}
            </Box>
          </Box>
          <Box
            sx={{
              height: "calc(100vh - 80px)",
            }}
          >
            <DataGrid
              rows={rankings}
              columns={columns}
              getRowId={(row) => row.id!}
              rowHeight={100}
              sx={{
                ...data_grid_styles,
              }}
            />
          </Box>
        </Box>
      )}

      {openView && (
        <Dialog
          sx={{
            "& .MuiPaper-root": {
              width: "400px",
            },
          }}
          open={!!openView}
          onClose={() => setOpenView(null)}
        >
          <DialogTitle
            color="primary"
            sx={{
              fontWeight: "bold",
            }}
          >
            Zodiac Ranking
          </DialogTitle>
          <DialogContent>
            <Typography
              sx={{
                fontSize: "14px",
              }}
            >
              <strong>Day</strong>: {openView.name}
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
              }}
            >
              <strong>Ranking</strong>:
            </Typography>
            <List>
              {openView.ranking.map((sign: any) => (
                <ListItem
                  key={sign}
                  sx={{
                    marginBottom: "4px",
                    padding: "4px 8px",
                    backgroundColor: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    cursor: "move",
                    color: "#2A4365",
                    fontSize: "14px",
                    "&:hover": {
                      "& span": { fontWeight: "bold" },
                    },
                  }}
                >
                  <ListItemText primary={sign} />
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button
              sx={{
                textTransform: "unset",
              }}
              onClick={() => setOpenView(null)}
              color="primary"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default RankingOfConstellations;
